import { FormatterType } from '@components/TableV2/table.types';
import { DescriptorType } from '../../../context/context.type';
import { FileParser } from '../types';

export enum DragItem {
  Parsed = 1,
  Entity = 2,
}

export enum MapFilterType {
  Mapped = 1,
  UnMapped = 2,
}

export interface MappingTreeNode {
  label: string;
  isMissing: boolean;
  children: MappingTreeNode[];
  isExplodedParent?: boolean;
  isExploded?: boolean;
  path?: string;
  root?: string;
}

export const FieldType = {
  Number: 'DOUBLE',
  Text: 'STRING',
  Date: 'DATE',
  DateTime: 'DATETIME',
  IP: 'IP',
  FIX: 'FIX',
  Boolean: 'BOOL',
  INT: 'INT',
  UINT32: 'UINT32',
  Message: 'MESSAGE',
  ENUM: 'ENUM',
};

export const FieldTypeEnum = {
  [FieldType.Text]: DescriptorType.TYPE_STRING,
  [FieldType.Number]: DescriptorType.TYPE_DOUBLE,
  [FieldType.Boolean]: DescriptorType.TYPE_BOOL,
  [FieldType.INT]: DescriptorType.TYPE_INT32,
  [FieldType.UINT32]: DescriptorType.TYPE_UINT32,
  [FieldType.ENUM]: DescriptorType.TYPE_ENUM,
  [FieldType.Date]: 'DATE',
  [FieldType.DateTime]: 'DATETIME',
  [FieldType.IP]: 'IP',
  [FieldType.Message]: 'MESSAGE',
  [DescriptorType.TYPE_STRING]: FieldType.Text,
  [DescriptorType.TYPE_DOUBLE]: FieldType.Number,
  [DescriptorType.TYPE_BOOL]: FieldType.Boolean,
  [DescriptorType.TYPE_INT32]: FieldType.INT,
  [DescriptorType.TYPE_UINT32]: FieldType.UINT32,
  [DescriptorType.TYPE_ENUM]: FieldType.ENUM,
  DATE: FieldType.Date,
  DATETIME: FieldType.DateTime,
  IP: FieldType.IP,
  FIX: FieldType.FIX,
  MESSAGE: FieldType.Message,
  FIELD: 'FIELD',
};
export enum SchemaTypes {
  STRING = 'string',
  BOOL = 'bool',
  DATE = 'date',
  DATETIME = 'datetime',
  IP = 'IP',
  FIX = 'FIX',
  NUMBER = 'number',
  FIELD = 'field',
  ENUM = 'enum',
}
export const typeLabelMap = {
  [FieldTypeEnum[FieldType.Text]]: SchemaTypes.STRING,
  TYPE_STRING: SchemaTypes.STRING,
  [FieldTypeEnum[FieldType.Boolean]]: SchemaTypes.BOOL,
  TYPE_BOOL: SchemaTypes.BOOL,
  [FieldTypeEnum[FieldType.Date]]: SchemaTypes.DATE,
  DATE: SchemaTypes.DATE,
  [FieldTypeEnum[FieldType.DateTime]]: SchemaTypes.DATETIME,
  DATETIME: SchemaTypes.DATETIME,
  [FieldTypeEnum[FieldType.IP]]: SchemaTypes.IP,
  IP: SchemaTypes.IP,
  [FieldTypeEnum[FieldType.FIX]]: SchemaTypes.FIX,
  FIX: SchemaTypes.FIX,
  [FieldTypeEnum[FieldType.Number]]: SchemaTypes.NUMBER,
  TYPE_DOUBLE: SchemaTypes.NUMBER,
  [FieldTypeEnum[FieldType.INT]]: SchemaTypes.NUMBER,
  TYPE_INT32: SchemaTypes.NUMBER,
  [FieldTypeEnum[FieldType.UINT32]]: SchemaTypes.NUMBER,
  TYPE_UINT32: SchemaTypes.NUMBER,
  [FieldTypeEnum.FIELD]: SchemaTypes.FIELD,
  TYPE_ENUM: SchemaTypes.ENUM,
  [FieldTypeEnum[FieldType.ENUM]]: SchemaTypes.ENUM,
};

export interface PreviewPopperData {
  anchor?: HTMLElement;
  field?: MappingTreeNode;
}

export interface DragItemType {
  type: DragItem;
  field: string;
}

export enum CopyMode {
  Hard = 'hard',
  Soft = 'soft',
}

export const PREVIEW_PAGE_SIZE = 40;

export enum MappingFieldType {
  Text = 'TEXT',
  Date = 'DATE',
  Number = 'NUMBER',
  Boolean = 'BOOLEAN',
  Array = 'ARRAY',
  Struct = 'STRUCT',
}

export enum MappingType {
  StringTemplate = 'STRING_TEMPLATE',
  Script = 'SCRIPT',
  Literal = 'VALUE',
  Path = 'PATH',
  Dictionary = 'DICTIONARY',
  Expression = 'EXPRESSION',
  Composite = 'COMPOSITE_VALUE',
}

export enum ModelSchemaType {
  BuiltinStatic = 'BUILTIN_STATIC',
  BuiltinDynamic = 'BUILTIN_DYNAMIC',
  CustomDynamic = 'CUSTOM_DYNAMIC',
}

export interface CompositeValueMapping {
  fields: FieldMapping[];
}

export interface BaseFieldMapping {
  defaultFieldName: string;
  modelFieldName: string;
  fieldType?: MappingFieldType;
  mappingType?: MappingType;
  entityName: string;
  key?: boolean;
  modelSchemaType: ModelSchemaType;
  exploded?: boolean;
  replicaNumber?: number;
  systemManaged?: boolean;
  syncedToDefault?: boolean;
}

export enum FieldValueType {
  MappingValue = 'MappingValue',
  CompositeValue = 'CompositeValue',
}

export enum FieldNameType {
  Model = 'Model',
  Metadata = 'Metadata',
}

export interface MappingValueMappingValue {
  fieldValueType: FieldValueType.MappingValue;
  mappingValue: string;
  compositeValue?: never;
}

export interface MappingValueCompositeValue {
  fieldValueType: FieldValueType.CompositeValue;
  compositeValue: CompositeValueMapping;
  mappingValue?: never;
}

export interface MappingModelName {
  fieldNameType: FieldNameType.Model;
  modelName: string;
  metadataName?: never;
}

export interface MappingMetadataName {
  fieldNameType: FieldNameType.Metadata;
  metadataName: string;
  modelName?: never;
}

export type FieldMapping = BaseFieldMapping &
  (MappingModelName | MappingMetadataName) &
  (MappingValueMappingValue | MappingValueCompositeValue);

export interface MappingValidationProps {
  previewId: string | undefined;
  type: string | undefined;
  instanceId: string | undefined;
  mapping: FieldMapping;
  reader?: FileParser['reader'];
  fileReaderConfig?: FileParser['fileReaderConfig'];
}

export type FileFieldTypesMap = Record<string, FormatterType>;
